import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, signal, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormControl, NG_VALUE_ACCESSOR, ControlValueAccessor, ReactiveFormsModule } from '@angular/forms';
import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { Subscription } from 'rxjs';
import { UiSafeStaff } from 'src/app/api/commonInterfaces';
import { IconWithMetadata, StaffIconMapFields, StaffService } from 'src/app/api/staff.service';
import { PreferencesService } from 'src/app/preferences.service';
import { IconService } from '../icon.service';
import { Select, SelectModule } from 'primeng/select';

import { StaffDisplayPipe } from '../pipes/staff-display.pipe';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TooltipModule } from 'primeng/tooltip';
import { StaffPhotoComponent } from 'src/app/components/staff/staff-photo/staff-photo.component';

@Component({
    selector: 'app-staff-dropdown',
    templateUrl: './staff-dropdown.component.html',
    styleUrls: ['./staff-dropdown.component.scss'],
    imports: [SelectModule, ReactiveFormsModule, StaffDisplayPipe, FontAwesomeModule, TooltipModule, StaffPhotoComponent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => StaffDropdownComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StaffDropdownComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {

  private _options: UiSafeStaff[] = []
  @Input() set options(value: UiSafeStaff[]) {
    this._options = this.staffService.sortStaff(value, this.preferences.staffDisplay)
  }

  get options(): UiSafeStaff[] {
    return this._options
  }

  @Input() allowUnassigned = true

  @Input() selectedStaffId: number = 0

  @Input() autoDisplayFirst = true

  @Input() formValueType: "UiSafeStaff" | "idString" = "UiSafeStaff"

  @Input() showOnInit = false

  @Input() iconFields: Array<keyof UiSafeStaff> = []

  @Input() virtualScroll: boolean = false

  @Input() virtualScrollItemSize: number | null = 38

  private _placeholder: string = ""
  @Input() set placeholder(value: string) {
    this._placeholder = value
  }

  get placeholder(): string {
    return this._placeholder
  }

  @Input()  iconMap: Map<number, IconWithMetadata[]> = new Map()

  @Output() change = new EventEmitter<UiSafeStaff | null>()



  form = new FormGroup({
    staff: new FormControl<UiSafeStaff | null>(null)
  })


  value: UiSafeStaff | null = null
  

  onChange: any = () => { };
  onTouched: any = () => { };
  disabled = false;
  touched = false;
  initialized = false;

  unassignedUser = this.staffService.getUnassignedUser()

  subscriptions: Subscription[] = []

  @ViewChild(Select) dropdown!: Select

  constructor(
    private staffService: StaffService,
    public preferences: PreferencesService,
    private chRef: ChangeDetectorRef,
    private icons: IconService
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(this.form.valueChanges.subscribe(changes => {
      if (changes.staff !== undefined) {
        this.value = changes.staff
        this.change.emit(this.value)

        switch (this.formValueType) {
          case "UiSafeStaff": {
            this.onChange(this.value)
            break;
          }
          case "idString": {
            this.onChange(this.value?.id.toString())
          }
        }

      }
    })
    )

    this.setSelectedStaff()

    if (this.showOnInit) {
      //feels hacky, but the dropdown doesn't show without it.  Maybe a better spot in the angular lifecycle to do it, but afterviewinit didn't work
      setTimeout(() => {
        this.dropdown.show()
      })


    }
    this.initialized = true
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options'] || changes['allowUnassigned']) {
      this.addUnassignedOption()
    }

    if (this.initialized && changes['selectedStaffId']) {
      this.setSelectedStaff()
    }

  }


  writeValue(obj: string | number | UiSafeStaff): void {
    if (typeof obj === "string") {
      this.value = this.options.find(u => u.id === Number.parseInt(obj, 10)) ?? null
    }

    if (typeof obj === "number") {
      this.value = this.options.find(u => u.id === obj) ?? null
    }

    if (typeof obj === "object") {
      this.value = obj
    }

    this.form.controls.staff.setValue(this.value)
    this.chRef.detectChanges()

  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled
    isDisabled ? this.form.disable() : this.form.enable()

  }

  addUnassignedOption() {
    if (this.allowUnassigned && !this._options.find(o => o.id === this.unassignedUser.id)) {
      this._options = [this.unassignedUser, ...this.options]
      this.chRef.detectChanges()
    }

    if (!this.allowUnassigned && this.options.find(o => o.id == this.unassignedUser.id)) {
      this._options = this.options.slice(1)
      if (this.value && this.value.id === this.unassignedUser.id) {
        this.value = null
      }
      this.chRef.detectChanges()
    }
  }

  setSelectedStaff() {
    const staff = this.options.find(o => o.id === this.selectedStaffId)
    if (staff) {
      this.form.controls.staff.setValue(staff)
      this.chRef.detectChanges()
    }
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched()
      this.touched = true
    }
  }

  show(isFocus?: any) {
    this.dropdown.show()
  }

  hide(isFocus?: any) {
    this.dropdown.hide()
  }

  focus() {
    this.dropdown.focus()
  }

  resetFilter() {
    this.dropdown.resetFilter()
  }


}
